import { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';

import {
	GetIssuesResponse,
	sortIssuesReleaseDate,
} from '../../../hooks/use-issue';
import { clone, Issue, Section } from '../../../constants';
import config from '../../../config';
import { useFetchPolicy } from '../../../App.hooks';

export function useGetSections(): GetSectionsResponse {
	const [sections, setSections] = useState<Array<Section>>([]);
	const fetchPolicy = useFetchPolicy(
		`sections`,
		config?.app?.cache?.ttl?.getAllIssues,
	);
	const { loading, error, data } = useQuery<{ sections: Array<Section> }>(
		GET_SECTIONS,
		{
			fetchPolicy,
		},
	);

	useEffect(() => {
		if (loading) return;
		if (error) {
			console.error(error);
			return;
		}
		if (!data) return;
		setSections(data.sections);
	}, [loading, error, data]);

	return {
		isLoading: loading,
		error: error ? new Error(error.message) : null,
		sections,
	};
}

interface GetSectionsResponse {
	isLoading: boolean;
	error: Error | null;
	sections: Array<Section>;
}

export function useGetIssues(): GetIssuesResponse {
	const [issues, setIssues] = useState<Array<Issue>>([]);
	const fetchPolicy = useFetchPolicy(
		`issues`,
		config?.app?.cache?.ttl?.getAllIssues,
	);

	const { loading, error, data } = useQuery<{ issues: Array<Issue> }>(
		GET_ISSUES,
		{
			fetchPolicy,
		},
	);

	useEffect(() => {
		if (loading) return;
		if (error) {
			console.error(error);
			return;
		}
		if (!data) return;
		setIssues(data.issues.map(clone));
	}, [loading, error, data]);

	return {
		isLoading: loading,
		error: error ? new Error(error.message) : null,
		issues: issues.sort(sortIssuesReleaseDate),
	};
}

const GET_ISSUES = gql`
	query GetIssues {
		issues {
			id
			name
			description
			cover
			externalUrl
			price
			index
			slug
			currency
			data
			updated
			created
			isPurchased
			releaseDate
		}
	}
`;

export const GET_SECTIONS = gql`
	{
		sections {
			id
			position
			name
			issues {
				id
				position
				name
				description
				cover
				externalUrl
				price
				features
				currency
				slug
				data
				releaseDate
				updated
				isPaid
				created
				isPurchased
			}
		}
	}
`;
